// src/pages/BillOfLadingPage.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import bolService from '../services/bolService';
import customerService from '../services/customerService';
import productService from '../services/productService';
import labelService from '../services/labelService';
import userService from '../services/userService';
import companyService from '../services/companyService';
import SearchableSelect from '../components/SearchableSelect';
import { usePermissions } from '../context/PermissionContext';
import { useLanguage, t } from '../context/LanguageContext';
import axios from 'axios';
import { addDays, parseISO, format } from 'date-fns';

// ── Constants ─────────────────────────────────────────────────────────────────

const EMPTY_ITEM = {
  productId: null,
  productName: '',
  lotCode: '',
  expirationDate: '',
  palletId: '',
  quantityCases: '',
  unitsPerCase: '',
  netWeightValue: '',
  netWeightUnit: 'lb',
  grossWeightValue: '',
  grossWeightUnit: 'lb',
  presentationId: null,
  sortOrder: 0,
};

const LB_TO_KG = 0.453592;
const KG_TO_LB = 2.20462;

const STATUS_BADGE = {
  draft:     { cls: 'bg-warning text-dark', label: 'Draft' },
  finalized: { cls: 'bg-success',           label: 'Finalized' },
  signed:    { cls: 'bg-primary',           label: 'Signed' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
}

function formatPalletId(fmt, n) {
  if (!fmt) return String(n);
  return fmt
    .replace(/{NNN}/g, String(n).padStart(3, '0'))
    .replace(/{NN}/g, String(n).padStart(2, '0'))
    .replace(/{N}/g, String(n));
}

// USDA/FSIS shipment attestation — mirrors bolAttestationComplete() in backend/routes/bolRoutes.js.
// This is UX only (avoids a round-trip 400); the backend gate is the real enforcement.
function bolHasUsdaLot(items, products) {
  const usdaProductIds = new Set(products.filter(p => p.regulatoryAgency === 'usda').map(p => String(p.id)));
  return (items || []).some(it => it.productId && usdaProductIds.has(String(it.productId)));
}

// Works for both the in-progress form state and a saved BOL from the API — both always carry
// `relabelingOccurred` as null (unanswered) / false / true. attestedBy/attestedAt are always
// server-stamped on save, so the toggle itself is the client-side signal of "has attested".
function isAttestationComplete(bol) {
  if (bol.usdaMarksPresent !== true) return false;
  if (bol.relabelingOccurred == null) return false;
  if (!bol.relabelingOccurred) return true;
  return !!(bol.csiNotifiedName && bol.csiNotifiedAt && bol.csiNotificationMethod && bol.marksVerifiedBy);
}

/** Safely evaluate a simple arithmetic expression (numbers + - * / parentheses only) */
function evalMath(str) {
  if (!str && str !== 0) return str;
  const s = String(str).trim();
  if (!/^[\d\s+\-*/.()\s]+$/.test(s)) return str;
  // must contain an operator to trigger eval
  if (!/[+\-*/]/.test(s)) return str;
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function('"use strict"; return (' + s + ')')();
    if (typeof result === 'number' && isFinite(result)) {
      return String(Math.round(result * 100) / 100);
    }
  } catch { /* ignore */ }
  return str;
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────

function ConfirmModal({ modal, onClose }) {
  const { lang } = useLanguage();
  if (!modal) return null;
  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className={`modal-header bg-${modal.variant || 'warning'} ${modal.variant === 'danger' ? 'text-white' : 'text-dark'}`}>
            <h5 className="modal-title">{modal.title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="mb-0">{modal.message}</p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>{t('Cancel', 'Cancelar', lang)}</button>
            <button
              className={`btn btn-${modal.variant || 'warning'}`}
              onClick={() => { modal.onConfirm(); onClose(); }}
            >
              {modal.confirmLabel || t('Confirm', 'Confirmar', lang)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// USDA/FSIS shipment attestation fields — shared by the edit modal and the pre-finalize/print
// re-confirmation gate below, so the exact same interactive control is what the user has to
// consciously engage with every time, not a passive read-only summary.
function UsdaAttestationFields({ value, onChange, userOptions, lang, readOnly = false, complete, establishmentNumber }) {
  const estLabel = establishmentNumber
    ? `EST ${establishmentNumber}`
    : t('your establishment number (not set — add it in Company Settings)', 'su número de establecimiento (no configurado — agréguelo en Configuración de la Empresa)', lang);

  return (
    <div className={`border rounded p-3 ${complete ? 'border-success' : 'border-danger'}`}>
      <div className="fw-semibold mb-2">
        ⚠ {t('USDA/FSIS Shipment Attestation — Required', 'Atestación de Envío USDA/FSIS — Requerida', lang)}
      </div>

      <div className="mb-3">
        <label className="form-label small mb-1">
          {t(`Do all the labels on this shipment show the USDA mark of inspection with ${estLabel}?`, `¿Todas las etiquetas de este envío muestran la marca de inspección USDA con ${estLabel}?`, lang)}
        </label>
        <div className="btn-group btn-group-sm d-block">
          <button
            type="button"
            disabled={readOnly}
            className={`btn ${value.usdaMarksPresent === false ? 'btn-danger' : 'btn-outline-secondary'}`}
            onClick={() => onChange('usdaMarksPresent', false)}
          >
            {t('No', 'No', lang)}
          </button>
          <button
            type="button"
            disabled={readOnly}
            className={`btn ${value.usdaMarksPresent === true ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => onChange('usdaMarksPresent', true)}
          >
            {t('Yes', 'Sí', lang)}
          </button>
        </div>
        {value.usdaMarksPresent === false && (
          <div className="text-danger mt-1" style={{ fontSize: 12 }}>
            {t('Do not ship. Correct the labels before finalizing or printing this BOL.', 'No envíe. Corrija las etiquetas antes de finalizar o imprimir este BOL.', lang)}
          </div>
        )}
      </div>

      <div className="mb-2">
        <label className="form-label small mb-1">
          {t('Did this lot undergo any container or label change after pre-shipment verification?', '¿Este lote sufrió algún cambio de contenedor o etiqueta después de la verificación previa al envío?', lang)}
        </label>
        <div className="btn-group btn-group-sm d-block">
          <button
            type="button"
            disabled={readOnly}
            className={`btn ${value.relabelingOccurred === false ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => onChange('relabelingOccurred', false)}
          >
            {t('No', 'No', lang)}
          </button>
          <button
            type="button"
            disabled={readOnly}
            className={`btn ${value.relabelingOccurred === true ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => onChange('relabelingOccurred', true)}
          >
            {t('Yes', 'Sí', lang)}
          </button>
        </div>
      </div>

      {value.relabelingOccurred === true && (
        <div className="row g-2">
          <div className="col-md-6">
            <label className="form-label small mb-1">{t('CSI Name', 'Nombre del CSI', lang)}</label>
            <input className="form-control form-control-sm" value={value.csiNotifiedName} disabled={readOnly}
              onChange={e => onChange('csiNotifiedName', e.target.value)}
              placeholder={t('e.g. Jane Smith', 'ej. Juana Pérez', lang)} />
          </div>
          <div className="col-md-6">
            <label className="form-label small mb-1">{t('Notified At', 'Notificado el', lang)}</label>
            <input type="datetime-local" className="form-control form-control-sm" value={value.csiNotifiedAt} disabled={readOnly}
              onChange={e => onChange('csiNotifiedAt', e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label small mb-1">{t('Notification Method', 'Método de Notificación', lang)}</label>
            <select className="form-select form-select-sm" value={value.csiNotificationMethod} disabled={readOnly}
              onChange={e => onChange('csiNotificationMethod', e.target.value)}>
              <option value="">{t('— Select —', '— Seleccionar —', lang)}</option>
              <option value="in_person">{t('In Person', 'En Persona', lang)}</option>
              <option value="phone">{t('Phone', 'Teléfono', lang)}</option>
              <option value="radio">{t('Radio', 'Radio', lang)}</option>
              <option value="other">{t('Other', 'Otro', lang)}</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label small mb-1">{t('USDA Marks Verified By', 'Marcas USDA Verificadas Por', lang)}</label>
            <SearchableSelect
              options={userOptions}
              value={value.marksVerifiedBy}
              onChange={val => onChange('marksVerifiedBy', val)}
              placeholder={t('Select employee…', 'Seleccionar empleado…', lang)}
              size="sm"
              disabled={readOnly}
            />
            <div className="form-text">
              {t('Confirms USDA marks of inspection were visually verified on the relabeled containers.', 'Confirma que las marcas de inspección USDA fueron verificadas visualmente en los contenedores reetiquetados.', lang)}
            </div>
          </div>
        </div>
      )}

      {!complete && (
        <div className="text-danger mt-2" style={{ fontSize: 12 }}>
          {t('Complete this attestation before the BOL can be finalized.', 'Complete esta atestación antes de que el BOL pueda finalizarse.', lang)}
        </div>
      )}
    </div>
  );
}

// Pre-finalize/print re-confirmation gate — shown every time Finalize or Print is invoked on a
// BOL with a USDA lot, regardless of whether the stored attestation already reads "complete".
// A BOL can sit in draft over several days; this is the last thing the user sees before it
// actually ships, so a stale attestation from day one can't silently ride along unchecked.
function UsdaAttestationGateModal({ gate, formValue, onChange, onConfirm, onCancel, userOptions, lang, saving, error, establishmentNumber }) {
  if (!gate) return null;
  const isForm = gate.mode === 'form';
  const value = isForm ? formValue : gate.local;
  const readOnly = !isForm && gate.bol.status !== 'draft';
  const complete = isAttestationComplete(value);
  const actionLabel = gate.action === 'finalize'
    ? t('Confirm & Finalize', 'Confirmar y Finalizar', lang)
    : t('Confirm & Print', 'Confirmar e Imprimir', lang);

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-danger text-white">
            <h5 className="modal-title">
              {t('Confirm Before Shipping', 'Confirmar Antes de Enviar', lang)}
              {gate.bol?.bolNumber ? ` — ${gate.bol.bolNumber}` : ''}
            </h5>
            <button className="btn-close btn-close-white" onClick={onCancel} disabled={saving} />
          </div>
          <div className="modal-body">
            <p className="text-muted" style={{ fontSize: 13 }}>
              {t('This shipment includes a USDA/FSIS-regulated lot. Review the attestation below before it ships.', 'Este envío incluye un lote regulado por USDA/FSIS. Revise la atestación a continuación antes de enviarlo.', lang)}
            </p>
            {readOnly && (
              <div className="alert alert-secondary py-2" style={{ fontSize: 12 }}>
                {t('This BOL is already finalized — the attestation is locked. Revert to draft first if it needs to change.', 'Este BOL ya está finalizado — la atestación está bloqueada. Revierta a borrador primero si necesita cambiarla.', lang)}
              </div>
            )}
            <UsdaAttestationFields
              value={value}
              onChange={onChange}
              userOptions={userOptions}
              lang={lang}
              readOnly={readOnly}
              complete={complete}
              establishmentNumber={establishmentNumber}
            />
            {error && <div className="alert alert-danger py-2 mt-2" style={{ fontSize: 13 }}>{error}</div>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>{t('Cancel', 'Cancelar', lang)}</button>
            <button className="btn btn-danger" onClick={onConfirm} disabled={saving || !complete}>
              {saving ? <span className="spinner-border spinner-border-sm" /> : actionLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function BillOfLadingPage() {
  const { lang } = useLanguage();
  const { hasModule } = usePermissions();
  const canEdit = hasModule('shipping.bol.edit');
  const canPrint = hasModule('shipping.bol.print');
  const canPalletLabels = hasModule('shipping.bol.pallet_labels');
  const canFinalize = hasModule('shipping.bol.finalize');
  const canDelete = hasModule('shipping.bol.delete');
  const navigate = useNavigate();
  const location = useLocation();

  // List state
  const [bols, setBols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [bolSettings, setBolSettings] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBol, setEditingBol] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState(null);

  // USDA/FSIS shipment attestation re-confirmation gate — shown right before Finalize/Print,
  // every time, regardless of whether the stored attestation is already "complete". See
  // UsdaAttestationGateModal below for why this can't just trust previously-saved data.
  const [gate, setGate] = useState(null);
  const [gateSaving, setGateSaving] = useState(false);
  const [gateError, setGateError] = useState('');

  // Customers + Products
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usdaEstablishmentNumber, setUsdaEstablishmentNumber] = useState('');

  // Lot code availability cache { [lotCode]: { produced, allocatedUnits, availableUnits } }
  const [lotAvailability, setLotAvailability] = useState({});
  const lotFetchRef = useRef({});

  // Lot code suggestions cache { [productId]: [lotCode, ...] }
  const [lotSuggestions, setLotSuggestions] = useState({});
  const lotSuggestFetchRef = useRef({});

  // Lot-code → production date cache (for expiration auto-fill)
  const lotDatesRef = useRef({});

  // Drag-to-reorder
  const dragIdxRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Form state
  const emptyForm = useCallback(() => ({
    bolNumber: '',
    shipDate: new Date().toISOString().slice(0, 10),
    shipVia: '',
    invoiceNumber: '',
    customerPO: '',
    consigneeName: '',
    consigneeAddress: '',
    consigneeId: '',
    shipToName: '',
    shipToAddress: '',
    sameAsConsignee: true,
    disclaimer: '',
    notes: '',
    items: [{ ...EMPTY_ITEM }],
    usdaMarksPresent: null,
    relabelingOccurred: null,
    csiNotifiedName: '',
    csiNotifiedAt: '',
    csiNotificationMethod: '',
    marksVerifiedBy: '',
  }), []);

  const [form, setForm] = useState(emptyForm);

  // ── Load data ───────────────────────────────────────────────────────────────

  const BOL_PAGE_SIZE = 30;
  const [bolPage, setBolPage] = useState(1);
  const [bolTotalPages, setBolTotalPages] = useState(1);
  const [bolTotalCount, setBolTotalCount] = useState(0);
  const [loadingMoreBols, setLoadingMoreBols] = useState(false);
  const [bolSearchInput, setBolSearchInput] = useState('');
  const [bolSearchQuery, setBolSearchQuery] = useState('');

  // Debounce free-text search — avoid firing a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setBolSearchQuery(bolSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [bolSearchInput]);

  const loadBols = useCallback(async (pageToLoad = 1, append = false) => {
    if (append) setLoadingMoreBols(true); else setLoading(true);
    try {
      const params = { page: pageToLoad, limit: BOL_PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (bolSearchQuery) params.search = bolSearchQuery;
      const data = await bolService.getBols(params);
      setBols(prev => (append ? [...prev, ...(data.bols || [])] : (data.bols || [])));
      setBolPage(data.page || 1);
      setBolTotalPages(data.totalPages || 1);
      setBolTotalCount(data.total || 0);
    } catch { /* ignore */ }
    finally { if (append) setLoadingMoreBols(false); else setLoading(false); }
  }, [statusFilter, bolSearchQuery]);

  const handleLoadMoreBols = () => loadBols(bolPage + 1, true);

  useEffect(() => { loadBols(1, false); }, [loadBols]);

  // Draft count for the filter pill — a lightweight dedicated fetch, not derived from the
  // (now paginated) `bols` list, which only holds the currently-loaded page(s).
  const [draftCount, setDraftCount] = useState(0);
  const fetchDraftCount = useCallback(async () => {
    try {
      const data = await bolService.getBols({ status: 'draft', limit: 1 });
      setDraftCount(data.total || 0);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchDraftCount(); }, [fetchDraftCount, bols]);

  useEffect(() => {
    bolService.getSettings().then(setBolSettings).catch(() => {});
    customerService.getCustomers().then(c => setCustomers(Array.isArray(c) ? c : [])).catch(() => {});
    productService.getProducts({ includeInactive: false, excludeIntermediate: true }).then(p => setProducts(Array.isArray(p) ? p : [])).catch(() => {});
    userService.listUsers().then(u => setCompanyUsers(Array.isArray(u) ? u : [])).catch(() => {});
    companyService.getCompanies()
      .then(data => {
        const company = Array.isArray(data) ? data[0] : data;
        setUsdaEstablishmentNumber(company?.usdaEstablishmentNumber || '');
      })
      .catch(() => {});
  }, []);

  // ── Lot code availability fetch ─────────────────────────────────────────────

  const fetchLotAvailability = useCallback(async (lotCode, productId) => {
    const cacheKey = `${lotCode}:${productId || ''}`;
    if (!lotCode || lotFetchRef.current[cacheKey]) return;
    lotFetchRef.current[cacheKey] = true;
    try {
      const data = await bolService.getLotCodeAvailability(lotCode, productId || null);
      setLotAvailability(prev => ({ ...prev, [cacheKey]: data }));
    } catch { /* ignore */ }
  }, []);

  // ── Lot code suggestions fetch ──────────────────────────────────────────────

  const fetchLotSuggestions = useCallback(async (productId) => {
    const key = productId || '__all__';
    if (lotSuggestFetchRef.current[key]) return;
    lotSuggestFetchRef.current[key] = true;
    try {
      const codes = await bolService.getLotCodeSuggestions(productId);
      setLotSuggestions(prev => ({ ...prev, [key]: codes }));
    } catch { /* ignore */ }
  }, []);

  // ── Item row drag-to-reorder ─────────────────────────────────────────────────

  const handleRowDragStart = (e, idx) => {
    dragIdxRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleRowDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const handleRowDrop = (e, targetIdx) => {
    e.preventDefault();
    const from = dragIdxRef.current;
    setDragOverIdx(null);
    dragIdxRef.current = null;
    if (from === null || from === targetIdx) return;
    setForm(f => {
      const items = [...f.items];
      const [moved] = items.splice(from, 1);
      items.splice(targetIdx, 0, moved);
      return { ...f, items: items.map((it, i) => ({ ...it, sortOrder: i })) };
    });
  };
  const handleRowDragEnd = () => {
    setDragOverIdx(null);
    dragIdxRef.current = null;
  };

  // ── Expiration auto-fill ────────────────────────────────────────────────────

  const autoFillExpiration = useCallback(async (idx, lotCode, productId) => {
    if (!lotCode || !productId) return;
    const product = products.find(p => String(p.id) === String(productId));
    if (!product?.shelfLifeDays) return;
    try {
      if (!(lotCode in lotDatesRef.current)) {
        const dateMap = await labelService.getLotCodeDates([lotCode]);
        Object.assign(lotDatesRef.current, dateMap);
      }
      const productionDateStr = lotDatesRef.current[lotCode];
      if (!productionDateStr) return;
      const expStr = format(addDays(parseISO(productionDateStr), product.shelfLifeDays), 'yyyy-MM-dd');
      setForm(f => {
        const items = [...f.items];
        if (!items[idx]?.expirationDate) {
          items[idx] = { ...items[idx], expirationDate: expStr };
          return { ...f, items };
        }
        return f;
      });
    } catch { /* ignore */ }
  }, [products]);

  // ── Modal helpers ───────────────────────────────────────────────────────────

  const openCreate = async () => {
    const f = emptyForm();
    try {
      const { bolNumber } = await bolService.getNextBolNumber();
      f.bolNumber = bolNumber;
    } catch { /* use empty */ }
    const weightUnit = bolSettings?.bolDefaultWeightUnit || 'lb';
    const palletFmt = bolSettings?.bolPalletIdFormat || '{N}';
    f.items[0].palletId = formatPalletId(palletFmt, 1);
    f.items[0].netWeightUnit = weightUnit;
    f.items[0].grossWeightUnit = weightUnit;
    setForm(f);
    setEditingBol(null);
    setModalError('');
    setModalOpen(true);
    fetchLotSuggestions(null);
  };

  const openCreateFromOrder = async (fromOrder) => {
    const f = emptyForm();
    try {
      const { bolNumber } = await bolService.getNextBolNumber();
      f.bolNumber = bolNumber;
    } catch { /* use empty */ }
    const weightUnit = bolSettings?.bolDefaultWeightUnit || 'lb';
    const palletFmt = bolSettings?.bolPalletIdFormat || '{N}';

    // Pre-fill consignee from the order customer
    if (fromOrder.customerId) {
      const c = customers.find(cu => String(cu.id) === String(fromOrder.customerId));
      if (c) {
        f.consigneeId = String(c.id);
        f.consigneeName = c.name;
        f.consigneeAddress = c.address || '';
      }
    } else if (fromOrder.customerName) {
      f.consigneeName = fromOrder.customerName;
    }

    // Map order items to BOL item rows
    const orderItems = fromOrder.items || [];
    f.items = orderItems.map((item, idx) => ({
      ...EMPTY_ITEM,
      productId: item.productId || null,
      productName: item.presentationLabel || item.Product?.name || '',
      quantityCases: item.quantityUnit === 'cases' ? String(item.quantity || '') : '',
      unitsPerCase: item.unitsPerCase ? String(item.unitsPerCase) : '',
      palletId: formatPalletId(palletFmt, idx + 1),
      netWeightUnit: weightUnit,
      grossWeightUnit: weightUnit,
      sortOrder: idx,
    }));
    if (f.items.length === 0) {
      f.items = [{ ...EMPTY_ITEM, palletId: formatPalletId(palletFmt, 1), netWeightUnit: weightUnit, grossWeightUnit: weightUnit }];
    }

    f.notes = `From order ${fromOrder.orderNumber}`;
    if (fromOrder.poNumber) f.customerPO = fromOrder.poNumber;
    if (fromOrder.qbInvoiceNumber) f.invoiceNumber = fromOrder.qbInvoiceNumber;

    setForm(f);
    setEditingBol(null);
    setModalError('');
    setModalOpen(true);

    // Clear location state so refreshing doesn't re-trigger
    window.history.replaceState({}, '', window.location.pathname);

    // Fetch lot suggestions for each product
    const productIds = new Set(f.items.map(it => it.productId).filter(Boolean));
    productIds.forEach(pid => fetchLotSuggestions(pid));
  };

  // Auto-open when navigated from an order
  const fromOrderHandledRef = useRef(false);
  useEffect(() => {
    const fromOrder = location.state?.fromOrder;
    if (!fromOrder || fromOrderHandledRef.current) return;
    // Wait until customers and bolSettings are loaded
    if (!customers.length || !bolSettings) return;
    fromOrderHandledRef.current = true;
    openCreateFromOrder(fromOrder);
  }, [location.state, customers, bolSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = async (bol) => {
    const full = await bolService.getBol(bol.id);
    setForm({
      bolNumber: full.bolNumber || '',
      shipDate: full.shipDate || '',
      shipVia: full.shipVia || '',
      invoiceNumber: full.invoiceNumber || '',
      customerPO: full.customerPO || '',
      consigneeName: full.consigneeName || '',
      consigneeAddress: full.consigneeAddress || '',
      consigneeId: full.consigneeId ? String(full.consigneeId) : '',
      shipToName: full.shipToName || '',
      shipToAddress: full.shipToAddress || '',
      sameAsConsignee: full.sameAsConsignee !== false,
      disclaimer: full.disclaimer || '',
      notes: full.notes || '',
      usdaMarksPresent: full.usdaMarksPresent ?? null,
      relabelingOccurred: full.relabelingOccurred ?? null,
      csiNotifiedName: full.csiNotifiedName || '',
      csiNotifiedAt: full.csiNotifiedAt ? full.csiNotifiedAt.slice(0, 16) : '',
      csiNotificationMethod: full.csiNotificationMethod || '',
      marksVerifiedBy: full.marksVerifiedBy ? String(full.marksVerifiedBy) : '',
      items: full.Items && full.Items.length > 0
        ? full.Items.map(it => ({
            id: it.id,
            productId: it.productId || null,
            productName: it.productName || '',
            lotCode: it.lotCode || '',
            expirationDate: it.expirationDate || '',
            palletId: it.palletId || '',
            quantityCases: it.quantityCases ?? '',
            unitsPerCase: it.unitsPerCase ?? '',
            netWeightValue: it.netWeightValue ?? '',
            netWeightUnit: it.netWeightUnit || 'lb',
            grossWeightValue: it.grossWeightValue ?? '',
            grossWeightUnit: it.grossWeightUnit || 'lb',
            presentationId: it.presentationId ?? null,
            sortOrder: it.sortOrder ?? 0,
          }))
        : [{ ...EMPTY_ITEM }],
    });
    setEditingBol(full);
    setModalError('');
    setModalOpen(true);
    (full.Items || []).forEach((it, idx) => {
      if (it.lotCode) fetchLotAvailability(it.lotCode, it.productId);
      if (it.productId) fetchLotSuggestions(it.productId);
      if (it.lotCode && it.productId && !it.expirationDate) autoFillExpiration(idx, it.lotCode, it.productId);
    });
  };

  const closeModal = () => { setModalOpen(false); setEditingBol(null); };

  // ── Form field helpers ──────────────────────────────────────────────────────

  const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const setItemField = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
    if (field === 'lotCode' && value) {
      autoFillExpiration(idx, value, form.items[idx]?.productId);
    }
    if (field === 'productId') {
      if (value) fetchLotSuggestions(value);
      const existingLot = form.items[idx]?.lotCode;
      if (existingLot) {
        fetchLotAvailability(existingLot, value || null);
        autoFillExpiration(idx, existingLot, value || null);
      }
    }
  };

  const handleItemWeightBlur = (idx, field) => {
    setForm(f => {
      const items = [...f.items];
      const evaled = evalMath(items[idx][field]);
      if (evaled !== items[idx][field]) {
        items[idx] = { ...items[idx], [field]: evaled };
        return { ...f, items };
      }
      return f;
    });
  };

  const addItem = () => {
    const distinctPallets = new Set(form.items.map(it => it.palletId).filter(Boolean)).size;
    const n = distinctPallets + 1;
    const palletId = formatPalletId(bolSettings?.bolPalletIdFormat || '{N}', n);
    const weightUnit = bolSettings?.bolDefaultWeightUnit || 'lb';
    const last = form.items[form.items.length - 1];
    setForm(f => ({
      ...f,
      items: [...f.items, {
        ...EMPTY_ITEM,
        productName: last?.productName || '',
        productId: last?.productId || null,
        lotCode: last?.lotCode || '',
        unitsPerCase: last?.unitsPerCase || '',
        palletId,
        netWeightUnit: weightUnit,
        grossWeightUnit: weightUnit,
        sortOrder: f.items.length,
      }],
    }));
  };

  const addProductGroup = () => {
    // Same pallet as the last row — just a different product on the same pallet
    const last = form.items[form.items.length - 1];
    const palletId = last?.palletId || '';
    const weightUnit = bolSettings?.bolDefaultWeightUnit || 'lb';
    setForm(f => ({
      ...f,
      items: [...f.items, {
        ...EMPTY_ITEM,
        palletId,
        netWeightUnit: weightUnit,
        grossWeightUnit: weightUnit,
        sortOrder: f.items.length,
      }],
    }));
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const handleCustomerSelect = (customerId) => {
    setField('consigneeId', customerId);
    if (customerId) {
      const c = customers.find(cu => String(cu.id) === String(customerId));
      if (c) {
        setForm(f => ({
          ...f,
          consigneeId: customerId,
          consigneeName: c.name,
          consigneeAddress: c.address || '',
        }));
      }
    }
  };

  // Auto-suggest net weight: unitNetWeight × cases × units/case
  const suggestNetWeight = (item) => {
    const prod = item.productId ? products.find(p => String(p.id) === String(item.productId)) : null;
    if (!prod?.unitNetWeightValue) return null;
    const cases = Number(item.quantityCases) || 0;
    const upc = Number(item.unitsPerCase) || 0;
    if (!cases || !upc) return null;
    let suggested = Math.round(Number(prod.unitNetWeightValue) * cases * upc * 10) / 10;
    let unit = prod.unitNetWeightUnit || 'lb';
    // Convert to item's current unit if different
    if (unit !== item.netWeightUnit) {
      suggested = unit === 'lb'
        ? Math.round(suggested * LB_TO_KG * 10) / 10
        : Math.round(suggested * KG_TO_LB * 10) / 10;
      unit = item.netWeightUnit;
    }
    return { value: String(suggested), unit };
  };

  // Auto-suggest gross weight: net + tare (wrapper + retail box + master case + pallet share)
  // All tare weights stored in kg → work in kg, then convert output to item's gross unit
  const suggestGrossWeight = (item, allItems) => {
    const net = Number(item.netWeightValue);
    if (!net || !item.netWeightUnit) return null;
    const prod = item.productId ? products.find(p => String(p.id) === String(item.productId)) : null;
    const pres = item.presentationId && prod?.Presentations
      ? prod.Presentations.find(p => String(p.id) === String(item.presentationId))
      : null;

    if (!pres && !prod?.wrapperWeightKg) return null;

    const cases = Number(item.quantityCases) || 0;
    const upc = Number(item.unitsPerCase) || 0;
    const totalUnits = cases * upc;

    let tareKg = 0;
    if (prod?.wrapperWeightKg && totalUnits > 0) tareKg += Number(prod.wrapperWeightKg) * totalUnits;
    if (pres) {
      const unitsPerBox = Number(pres.unitsPerRetailBox) || 1;
      const retailBoxes = totalUnits > 0 ? Math.ceil(totalUnits / unitsPerBox) : cases;
      if (pres.retailBoxWeightKg) tareKg += Number(pres.retailBoxWeightKg) * retailBoxes;
      if (pres.masterCaseWeightKg) tareKg += Number(pres.masterCaseWeightKg) * cases;
    }

    // Pallet tare: shared proportionally across rows with same palletId
    const palletWtKg = bolSettings?.palletWeightKg != null ? Number(bolSettings.palletWeightKg) : 18.14;
    if (item.palletId && cases > 0) {
      const palletCases = allItems
        .filter(it => it.palletId === item.palletId)
        .reduce((s, it) => s + (Number(it.quantityCases) || 0), 0);
      if (palletCases > 0) tareKg += palletWtKg * (cases / palletCases);
    }

    // Convert net to kg, add tare, convert output to item's gross unit
    const netKg = item.netWeightUnit === 'lb' ? net * LB_TO_KG : net;
    const grossKg = netKg + tareKg;
    let gross, unit;
    if (item.grossWeightUnit === 'lb') {
      gross = Math.round(grossKg * KG_TO_LB * 10) / 10;
      unit = 'lb';
    } else {
      gross = Math.round(grossKg * 10) / 10;
      unit = 'kg';
    }
    return { value: String(gross), unit };
  };

  // ── Computed totals ─────────────────────────────────────────────────────────

  const totals = useMemo(() => {
    let cases = 0, netLb = 0, netKg = 0, grossLb = 0, grossKg = 0;
    for (const it of form.items) {
      cases += Number(it.quantityCases) || 0;
      const net = Number(it.netWeightValue) || 0;
      const gross = Number(it.grossWeightValue) || 0;
      if (it.netWeightUnit === 'lb') netLb += net; else netKg += net;
      if (it.grossWeightUnit === 'lb') grossLb += gross; else grossKg += gross;
    }
    const netStr = [netLb ? `${netLb.toLocaleString()} lb` : '', netKg ? `${netKg.toLocaleString()} kg` : ''].filter(Boolean).join(' + ') || '—';
    const grossStr = [grossLb ? `${grossLb.toLocaleString()} lb` : '', grossKg ? `${grossKg.toLocaleString()} kg` : ''].filter(Boolean).join(' + ') || '—';
    return { cases, netStr, grossStr };
  }, [form.items]);

  // ── Pallet tare summary (for info banner) ──────────────────────────────────

  const palletTareSummary = useMemo(() => {
    const palletWtKg = bolSettings?.palletWeightKg != null ? Number(bolSettings.palletWeightKg) : 18.14;
    // Group rows by palletId
    const groups = {};
    form.items.forEach(it => {
      if (!it.palletId) return;
      if (!groups[it.palletId]) groups[it.palletId] = { totalCases: 0, rows: [] };
      const c = Number(it.quantityCases) || 0;
      groups[it.palletId].totalCases += c;
      groups[it.palletId].rows.push(c);
    });
    // Build one line per pallet that has >0 cases
    const lines = Object.entries(groups)
      .filter(([, g]) => g.totalCases > 0)
      .map(([palletId, g]) => {
        const shares = g.rows.map(c =>
          g.totalCases > 0 ? Math.round(palletWtKg * (c / g.totalCases) * 10) / 10 : 0
        );
        return { palletId, totalCases: g.totalCases, shares };
      });
    return { palletWtKg, lines };
  }, [form.items, bolSettings]);

  // ── Save ────────────────────────────────────────────────────────────────────

  // Full, safe update payload from current form state — always sends every header field
  // (not just attestation) so a partial PATCH never risks clearing unrelated BOL data.
  const buildPayload = () => ({
    ...form,
    consigneeId: form.consigneeId ? Number(form.consigneeId) : null,
    marksVerifiedBy: form.marksVerifiedBy ? Number(form.marksVerifiedBy) : null,
    items: form.items.map((it, idx) => ({
      ...it,
      productId: it.productId ? Number(it.productId) : null,
      quantityCases: Number(it.quantityCases) || 0,
      unitsPerCase: it.unitsPerCase ? Number(it.unitsPerCase) : null,
      netWeightValue: it.netWeightValue !== '' ? Number(it.netWeightValue) : null,
      grossWeightValue: it.grossWeightValue !== '' ? Number(it.grossWeightValue) : null,
      expirationDate: it.expirationDate || null,
      presentationId: it.presentationId ? Number(it.presentationId) : null,
      sortOrder: idx,
    })),
  });

  const handleSave = async (andFinalize = false) => {
    if (!form.bolNumber.trim()) { setModalError(t('BOL number is required', 'El número de BOL es requerido', lang)); return; }
    if (!form.consigneeName.trim()) { setModalError(t('Consignee name is required', 'El nombre del consignatario es requerido', lang)); return; }
    const validItems = form.items.filter(it => it.productName && it.lotCode);
    if (validItems.length === 0) { setModalError(t('Add at least one item with a product name and lot code', 'Agrega al menos un artículo con nombre de producto y código de lote', lang)); return; }

    setSaving(true);
    setModalError('');
    try {
      const payload = buildPayload();

      let saved;
      if (editingBol) {
        saved = await bolService.updateBol(editingBol.id, payload);
      } else {
        saved = await bolService.createBol(payload);
      }

      if (andFinalize) {
        if (formHasUsdaLot) {
          // Even though the attestation section above may already show "complete" — it could
          // have been filled in days ago while this BOL sat in draft. Require an explicit
          // re-confirmation, right now, as the very last step before it becomes official.
          setGateError('');
          setGate({ mode: 'form', bol: saved, action: 'finalize' });
          return;
        }
        await bolService.finalizeBol(saved.id);
      }

      closeModal();
      loadBols();
    } catch (err) {
      setModalError(err?.response?.data?.message || t('Error saving BOL', 'Error al guardar el BOL', lang));
    } finally {
      setSaving(false);
    }
  };

  // ── USDA/FSIS shipment attestation re-confirmation gate ────────────────────────
  // Opened for BOTH Finalize and Print whenever the BOL carries a USDA lot — every time,
  // not just the first time — so a stale attestation from days ago can't silently ride along.

  const closeGate = () => { setGate(null); setGateError(''); };

  const openGateStandalone = (bol, action) => {
    setGateError('');
    setGate({
      mode: 'standalone',
      action,
      bol,
      local: {
        usdaMarksPresent: bol.usdaMarksPresent ?? null,
        relabelingOccurred: bol.relabelingOccurred ?? null,
        csiNotifiedName: bol.csiNotifiedName || '',
        csiNotifiedAt: bol.csiNotifiedAt ? bol.csiNotifiedAt.slice(0, 16) : '',
        csiNotificationMethod: bol.csiNotificationMethod || '',
        marksVerifiedBy: bol.marksVerifiedBy ? String(bol.marksVerifiedBy) : '',
      },
    });
  };

  const handleGateChange = (field, value) => {
    if (gate?.mode === 'form') {
      setField(field, value); // form mode edits the shared form state directly
    } else {
      setGate(g => ({ ...g, local: { ...g.local, [field]: value } }));
    }
  };

  const handleGateConfirm = async () => {
    if (!gate) return;
    setGateSaving(true);
    setGateError('');
    try {
      if (gate.mode === 'form') {
        const saved = await bolService.updateBol(gate.bol.id, buildPayload());
        await bolService.finalizeBol(saved.id);
        setGate(null);
        closeModal();
        loadBols();
        return;
      }

      // standalone mode — bol wasn't already open in the edit form
      const { bol, action, local } = gate;
      if (bol.status === 'draft') {
        await bolService.updateBol(bol.id, {
          shipDate: bol.shipDate, shipVia: bol.shipVia, invoiceNumber: bol.invoiceNumber, customerPO: bol.customerPO,
          consigneeName: bol.consigneeName, consigneeAddress: bol.consigneeAddress, consigneeId: bol.consigneeId,
          shipToName: bol.shipToName, shipToAddress: bol.shipToAddress, sameAsConsignee: bol.sameAsConsignee,
          disclaimer: bol.disclaimer, notes: bol.notes,
          usdaMarksPresent: local.usdaMarksPresent === true,
          relabelingOccurred: local.relabelingOccurred,
          csiNotifiedName: local.csiNotifiedName,
          csiNotifiedAt: local.csiNotifiedAt,
          csiNotificationMethod: local.csiNotificationMethod,
          marksVerifiedBy: local.marksVerifiedBy ? Number(local.marksVerifiedBy) : null,
        });
      }
      if (action === 'finalize') {
        await bolService.finalizeBol(bol.id);
        loadBols();
      } else {
        window.open(`/bol/print?id=${bol.id}`, '_blank');
      }
      setGate(null);
    } catch (err) {
      setGateError(err?.response?.data?.message || t('Error', 'Error', lang));
    } finally {
      setGateSaving(false);
    }
  };

  const handleFinalize = (bol) => {
    if (bolHasUsdaLot(bol.Items, products)) {
      openGateStandalone(bol, 'finalize');
      return;
    }
    setConfirmModal({
      title: t('Finalize BOL', 'Finalizar BOL', lang),
      message: t(`Finalize BOL ${bol.bolNumber}? This will lock it and record the shipment in traceability.`, `¿Finalizar el BOL ${bol.bolNumber}? Esto lo bloqueará y registrará el envío en trazabilidad.`, lang),
      variant: 'success',
      confirmLabel: t('Finalize', 'Finalizar', lang),
      onConfirm: async () => {
        try {
          await bolService.finalizeBol(bol.id);
          loadBols();
        } catch (err) {
          alert(err?.response?.data?.message || t('Error finalizing BOL', 'Error al finalizar el BOL', lang));
        }
      },
    });
  };

  const handleUnfinalize = (bol) => {
    setConfirmModal({
      title: t('Revert to Draft', 'Revertir a Borrador', lang),
      message: t(`Revert BOL ${bol.bolNumber} to draft? This will delete the associated shipment traceability records.`, `¿Revertir el BOL ${bol.bolNumber} a borrador? Esto eliminará los registros de trazabilidad del envío.`, lang),
      variant: 'danger',
      confirmLabel: t('Revert to Draft', 'Revertir a Borrador', lang),
      onConfirm: async () => {
        try {
          await bolService.unfinalizeBol(bol.id);
          loadBols();
        } catch (err) {
          alert(err?.response?.data?.message || t('Error unfinalizing BOL', 'Error al revertir el BOL', lang));
        }
      },
    });
  };

  const handleDelete = (bol) => {
    setConfirmModal({
      title: t('Delete BOL', 'Eliminar BOL', lang),
      message: t(`Delete draft BOL ${bol.bolNumber}? This cannot be undone.`, `¿Eliminar el BOL borrador ${bol.bolNumber}? Esta acción no se puede deshacer.`, lang),
      variant: 'danger',
      confirmLabel: t('Delete', 'Eliminar', lang),
      onConfirm: async () => {
        try {
          await bolService.deleteBol(bol.id);
          loadBols();
        } catch (err) {
          alert(err?.response?.data?.message || t('Error deleting BOL', 'Error al eliminar el BOL', lang));
        }
      },
    });
  };

  // ── Signed scan upload ──────────────────────────────────────────────────────

  const signedFileRef = useRef();
  const handleSignedUpload = async (bol) => {
    signedFileRef.current._bolId = bol.id;
    signedFileRef.current.click();
  };

  const handleSignedFileChange = async (e) => {
    const file = e.target.files?.[0];
    const bolId = signedFileRef.current._bolId;
    if (!file || !bolId) return;
    try {
      const { uploadUrl, publicUrl } = await bolService.getSignedUploadUrl(bolId, file.name, file.type);
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });
      await bolService.confirmSignedUpload(bolId, publicUrl);
      loadBols();
    } catch {
      alert(t('Failed to upload signed document', 'Error al subir el documento firmado', lang));
    } finally {
      if (signedFileRef.current) signedFileRef.current.value = '';
    }
  };

  // ── Print / Preview ─────────────────────────────────────────────────────────

  const handlePrint = (bol) => {
    if (bolHasUsdaLot(bol.Items, products)) {
      openGateStandalone(bol, 'print');
      return;
    }
    window.open(`/bol/print?id=${bol.id}`, '_blank');
  };

  // Build a pseudo-BOL object from current form state for preview
  const formAsBol = useMemo(() => ({
    ...form,
    consigneeId: form.consigneeId ? Number(form.consigneeId) : null,
    Items: form.items.map((it, idx) => ({
      ...it,
      id: it.id || idx,
      quantityCases: Number(it.quantityCases) || 0,
      netWeightValue: it.netWeightValue !== '' ? Number(it.netWeightValue) : null,
      grossWeightValue: it.grossWeightValue !== '' ? Number(it.grossWeightValue) : null,
    })),
  }), [form]);

  // USDA/FSIS shipment attestation — required whenever the BOL carries a USDA-classified lot
  const formHasUsdaLot = useMemo(() => bolHasUsdaLot(form.items, products), [form.items, products]);
  const formAttestationComplete = useMemo(() => isAttestationComplete(form), [form]);
  const userOptions = useMemo(() => companyUsers.map(u => ({ value: String(u.id), label: u.username })), [companyUsers]);

  // ── Render ──────────────────────────────────────────────────────────────────

  const shipViaOptions = bolSettings?.bolShipViaOptions || [];

  return (
    <div className="container-fluid mt-3" style={{ maxWidth: 1400 }}>
      <div>
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h4 className="mb-0">{t('Bills of Lading', 'Cartas de Porte', lang)}</h4>
          {canEdit && <button className="btn btn-primary btn-sm" onClick={openCreate}>+ {t('New BOL', 'Nuevo BOL', lang)}</button>}
        </div>

        {/* Status filter pills + search */}
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          {[['all', t('All', 'Todos', lang)], ['draft', `${t('Draft', 'Borrador', lang)}${draftCount > 0 ? ` (${draftCount})` : ''}`], ['finalized', t('Finalized', 'Finalizado', lang)], ['signed', t('Signed', 'Firmado', lang)]].map(([val, label]) => (
            <button
              key={val}
              className={`btn btn-sm ${statusFilter === val ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setStatusFilter(val)}
            >
              {label}
            </button>
          ))}
          <input
            type="text"
            className="form-control form-control-sm ms-auto"
            style={{ maxWidth: 280 }}
            placeholder={t('Search by BOL #, PO, invoice #, or consignee…', 'Buscar por # BOL, PO, factura o consignatario…', lang)}
            value={bolSearchInput}
            onChange={e => setBolSearchInput(e.target.value)}
          />
        </div>

        {/* BOL list */}
        {loading ? (
          <div className="text-center py-4"><span className="spinner-border" /></div>
        ) : bols.length === 0 ? (
          <div className="text-muted text-center py-4">{t('No bills of lading found.', 'No se encontraron cartas de porte.', lang)}</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover table-sm align-middle">
              <thead className="table-light">
                <tr>
                  <th>BOL #</th>
                  <th>{t('Ship Date', 'Fecha Envío', lang)}</th>
                  <th>{t('Consignee', 'Consignatario', lang)}</th>
                  <th>{t('Ship Via', 'Vía de Envío', lang)}</th>
                  <th className="text-center">{t('Pallets', 'Pallets', lang)}</th>
                  <th className="text-center">{t('Cases', 'Cajas', lang)}</th>
                  <th>{t('Status', 'Estado', lang)}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {bols.map(bol => {
                  const badge = STATUS_BADGE[bol.status] || STATUS_BADGE.draft;
                  const pallets = bol.Items?.length > 0 ? new Set(bol.Items.map(it => it.palletId).filter(Boolean)).size : 0;
                  const cases = bol.Items?.reduce((s, it) => s + (it.quantityCases || 0), 0) || 0;
                  return (
                    <tr key={bol.id}>
                      <td className="fw-semibold font-monospace">{bol.bolNumber}</td>
                      <td>{fmtDate(bol.shipDate)}</td>
                      <td>{bol.consigneeName || '—'}</td>
                      <td><span className="text-muted small">{bol.shipVia || '—'}</span></td>
                      <td className="text-center">{pallets}</td>
                      <td className="text-center">{cases}</td>
                      <td><span className={`badge ${badge.cls}`}>{t(badge.label, { Draft: 'Borrador', Finalized: 'Finalizado', Signed: 'Firmado' }[badge.label] || badge.label, lang)}</span></td>
                      <td>
                        <div className="d-flex gap-1 justify-content-end">
                          {bol.status === 'draft' && (
                            <>
                              {canEdit && <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(bol)}>{t('Edit', 'Editar', lang)}</button>}
                              {canPrint && <button className="btn btn-outline-primary btn-sm" onClick={() => handlePrint(bol)}>{t('Print BOL', 'Imprimir BOL', lang)}</button>}
                              {canPalletLabels && <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/bol/${bol.id}/pallet-labels`)}>{t('Pallet Labels', 'Etiquetas de Pallet', lang)}</button>}
                              {canFinalize && <button className="btn btn-success btn-sm" onClick={() => handleFinalize(bol)}>{t('Finalize', 'Finalizar', lang)}</button>}
                              {canDelete && <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(bol)}>{t('Delete', 'Eliminar', lang)}</button>}
                            </>
                          )}
                          {(bol.status === 'finalized' || bol.status === 'signed') && (
                            <>
                              {canPrint && <button className="btn btn-outline-primary btn-sm" onClick={() => handlePrint(bol)}>{t('Print BOL', 'Imprimir BOL', lang)}</button>}
                              {canPalletLabels && <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/bol/${bol.id}/pallet-labels`)}>{t('Pallet Labels', 'Etiquetas de Pallet', lang)}</button>}
                              {canEdit && bol.status === 'finalized' && (
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => handleSignedUpload(bol)}>{t('Upload Signed', 'Subir Firmado', lang)}</button>
                              )}
                              {bol.scannedDocumentUrl && (
                                <a className="btn btn-outline-success btn-sm" href={bol.scannedDocumentUrl} target="_blank" rel="noreferrer">{t('Signed PDF', 'PDF Firmado', lang)}</a>
                              )}
                              {canDelete && <button className="btn btn-outline-warning btn-sm" onClick={() => handleUnfinalize(bol)}>{t('Revert', 'Revertir', lang)}</button>}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-center text-muted mt-2 mb-2" style={{ fontSize: 13 }}>
              {t('Showing', 'Mostrando', lang)} {bols.length} {t('of', 'de', lang)} {bolTotalCount}
            </div>
            {bolPage < bolTotalPages && (
              <div className="d-flex justify-content-center mb-4">
                <button
                  className="btn btn-outline-primary btn-sm px-4"
                  style={{ borderRadius: 20 }}
                  onClick={handleLoadMoreBols}
                  disabled={loadingMoreBols}
                >
                  {loadingMoreBols
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('Loading…', 'Cargando…', lang)}</>
                    : t('Load 30 more', 'Cargar 30 más', lang)}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Hidden signed file input ── */}
      <input ref={signedFileRef} type="file" accept=".pdf,image/*" className="d-none" onChange={handleSignedFileChange} />

      {/* ── Confirm modal ── */}
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
      <UsdaAttestationGateModal
        gate={gate}
        formValue={form}
        onChange={handleGateChange}
        onConfirm={handleGateConfirm}
        onCancel={closeGate}
        userOptions={userOptions}
        lang={lang}
        saving={gateSaving}
        error={gateError}
        establishmentNumber={usdaEstablishmentNumber}
      />

      {/* ── Create/Edit Modal ── */}
      {modalOpen && (
        <div className="modal show d-block d-print-none" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-fullscreen-sm-down modal-dialog-scrollable" style={{ maxWidth: 1400 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingBol ? `${t('Edit BOL', 'Editar BOL', lang)} ${editingBol.bolNumber}` : t('New Bill of Lading', 'Nueva Carta de Porte', lang)}</h5>
                <button className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                {modalError && <div className="alert alert-danger py-2">{modalError}</div>}

                <div className="row g-3">
                  {/* BOL # */}
                  <div className="col-md-3">
                    <label className="form-label small mb-1">{t('BOL Number', 'Número de BOL', lang)}</label>
                    <input className="form-control form-control-sm font-monospace" value={form.bolNumber} onChange={e => setField('bolNumber', e.target.value)} />
                  </div>
                  {/* Ship Date */}
                  <div className="col-md-3">
                    <label className="form-label small mb-1">{t('Ship Date', 'Fecha de Envío', lang)}</label>
                    <input type="date" className="form-control form-control-sm" value={form.shipDate} onChange={e => setField('shipDate', e.target.value)} />
                  </div>
                  {/* Ship Via */}
                  <div className="col-md-3">
                    <label className="form-label small mb-1">{t('Ship Via', 'Vía de Envío', lang)}</label>
                    <select className="form-select form-select-sm" value={form.shipVia} onChange={e => setField('shipVia', e.target.value)}>
                      <option value="">{t('Select…', 'Seleccionar…', lang)}</option>
                      {shipViaOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  {/* Invoice # */}
                  <div className="col-md-3">
                    <label className="form-label small mb-1">{t('Invoice #', 'Factura #', lang)} <span className="text-muted">({t('optional', 'opcional', lang)})</span></label>
                    <input className="form-control form-control-sm" value={form.invoiceNumber} onChange={e => setField('invoiceNumber', e.target.value)} />
                  </div>
                  {/* Customer PO */}
                  <div className="col-12">
                    <label className="form-label small mb-1">{t('Customer PO(s)', 'OC del Cliente', lang)} <span className="text-muted">({t('one per line', 'uno por línea', lang)})</span></label>
                    <textarea className="form-control form-control-sm" rows={2} value={form.customerPO} onChange={e => setField('customerPO', e.target.value)} />
                  </div>

                  {/* Consignee */}
                  <div className="col-12"><hr className="my-1" /><small className="text-muted fw-semibold">{t('CONSIGNEE', 'CONSIGNATARIO', lang)}</small></div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">{t('Select Customer', 'Seleccionar Cliente', lang)} <span className="text-muted">({t('optional', 'opcional', lang)})</span></label>
                    <select className="form-select form-select-sm" value={form.consigneeId} onChange={e => handleCustomerSelect(e.target.value)}>
                      <option value="">{t('Manual entry…', 'Entrada manual…', lang)}</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">{t('Consignee Name', 'Nombre del Consignatario', lang)} <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={form.consigneeName} onChange={e => setField('consigneeName', e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small mb-1">{t('Consignee Address', 'Dirección del Consignatario', lang)}</label>
                    <textarea className="form-control form-control-sm" rows={2} value={form.consigneeAddress} onChange={e => setField('consigneeAddress', e.target.value)} />
                  </div>

                  {/* Ship To */}
                  <div className="col-12">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="sameAsConsignee" checked={form.sameAsConsignee} onChange={e => setField('sameAsConsignee', e.target.checked)} />
                      <label className="form-check-label small" htmlFor="sameAsConsignee">{t('Ship To = Consignee', 'Enviar A = Consignatario', lang)}</label>
                    </div>
                  </div>
                  {!form.sameAsConsignee && (
                    <>
                      <div className="col-md-4">
                        <label className="form-label small mb-1">{t('Ship To Name', 'Nombre de Destino', lang)}</label>
                        <input className="form-control form-control-sm" value={form.shipToName} onChange={e => setField('shipToName', e.target.value)} />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label small mb-1">{t('Ship To Address', 'Dirección de Destino', lang)}</label>
                        <textarea className="form-control form-control-sm" rows={2} value={form.shipToAddress} onChange={e => setField('shipToAddress', e.target.value)} />
                      </div>
                    </>
                  )}

                  {/* Items table */}
                  <div className="col-12"><hr className="my-1" /><small className="text-muted fw-semibold">{t('ITEMS (PALLETS)', 'ARTÍCULOS (PALLETS)', lang)}</small></div>
                  <div className="col-12">
                    <div className="d-flex align-items-start gap-2 px-1 py-1 mb-1 rounded" style={{ background: '#f0f4ff', fontSize: 12, color: '#444' }}>
                      <span>🪣</span>
                      <div style={{ flex: 1 }}>
                        <span className="fw-semibold">{t('Pallet tare', 'Tara de pallet', lang)}: {palletTareSummary.palletWtKg} kg/pallet</span>
                        {palletTareSummary.palletWtKg > 0 && palletTareSummary.lines.length > 0 && (
                          <span className="text-muted ms-2">
                            ({palletTareSummary.lines.map(l =>
                              `${l.palletId}: ${l.shares.join(' + ')} kg`
                            ).join(' · ')})
                          </span>
                        )}
                        {palletTareSummary.palletWtKg === 0 && (
                          <span className="text-muted ms-1">({t('no pallet tare applied', 'sin tara de pallet', lang)})</span>
                        )}
                        <span className="ms-2 text-muted">— {t('distributed proportionally by case count per Pallet ID', 'distribuido proporcionalmente por cajas por ID de pallet', lang)}. </span>
                        <button
                          className="btn btn-link p-0 align-baseline"
                          style={{ fontSize: 12 }}
                          onClick={() => { closeModal(); navigate('/admin/settings'); }}
                        >
                          {t('Change in Company Settings', 'Cambiar en Configuración de Empresa', lang)} ↗
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div style={{ overflowX: 'auto' }}>
                      <table className="table table-sm table-bordered" style={{ minWidth: 1200, tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: 24 }} />
                          <col style={{ width: 175 }} />
                          <col style={{ width: 160 }} />
                          <col style={{ width: 135 }} />
                          <col style={{ width: 110 }} />
                          <col style={{ width: 82 }} />
                          <col style={{ width: 74 }} />
                          <col style={{ width: 82 }} />
                          <col style={{ width: 150 }} />
                          <col style={{ width: 150 }} />
                          <col style={{ width: 34 }} />
                        </colgroup>
                        <thead className="table-light">
                          <tr>
                            <th />
                            <th className="small">{t('Product (BOL name)', 'Producto (nombre BOL)', lang)}</th>
                            <th className="small">{t('Production Product', 'Producto de Producción', lang)}</th>
                            <th className="small">{t('Lot Code', 'Código de Lote', lang)}</th>
                            <th className="small">{t('Exp. Date', 'F. Venc.', lang)}</th>
                            <th className="small text-center">{t('Pallet ID', 'ID Pallet', lang)}</th>
                            <th className="small text-center">{t('Cases', 'Cajas', lang)}</th>
                            <th className="small text-center">{t('Units/Case', 'Unid/Caja', lang)}</th>
                            <th className="small text-center">{t('Net Wgt', 'Peso Neto', lang)}</th>
                            <th className="small text-center">{t('Gross Wgt', 'Peso Bruto', lang)}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.items.map((item, idx) => {
                            const avail = lotAvailability[`${item.lotCode}:${item.productId || ''}`];
                            const totalUnits = (Number(item.quantityCases) || 0) * (Number(item.unitsPerCase) || 0);
                            const netSuggestion = suggestNetWeight(item);
                            const grossSuggestion = item.netWeightValue !== '' ? suggestGrossWeight(item, form.items) : null;
                            const suggestKey = item.productId || '__all__';
                            const suggestions = lotSuggestions[suggestKey] || [];
                            const datalistId = `lotcodes-${idx}`;
                            const prod = item.productId ? products.find(p => String(p.id) === String(item.productId)) : null;
                            const prodPresentations = prod?.Presentations || [];
                            return (
                              <tr
                                key={idx}
                                draggable
                                onDragStart={e => handleRowDragStart(e, idx)}
                                onDragOver={e => handleRowDragOver(e, idx)}
                                onDrop={e => handleRowDrop(e, idx)}
                                onDragEnd={handleRowDragEnd}
                                style={{
                                  verticalAlign: 'top',
                                  opacity: dragIdxRef.current === idx ? 0.4 : 1,
                                  background: dragOverIdx === idx ? '#e8f4ff' : undefined,
                                }}
                              >
                                {/* Drag handle */}
                                <td style={{ textAlign: 'center', cursor: 'grab', color: '#adb5bd', userSelect: 'none', paddingTop: 8 }}>⠿</td>
                                {/* Product BOL name */}
                                <td>
                                  <input
                                    className="form-control form-control-sm"
                                    placeholder={t('Commercial name…', 'Nombre comercial…', lang)}
                                    value={item.productName}
                                    onChange={e => setItemField(idx, 'productName', e.target.value)}
                                  />
                                </td>
                                {/* Production Product */}
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={item.productId || ''}
                                    onChange={e => {
                                      const pid = e.target.value;
                                      const prod = products.find(p => String(p.id) === pid);
                                      setForm(f => {
                                        const items = [...f.items];
                                        items[idx] = {
                                          ...items[idx],
                                          productId: pid || null,
                                          productName: items[idx].productName || prod?.name || '',
                                        };
                                        return { ...f, items };
                                      });
                                      if (pid) fetchLotSuggestions(pid);
                                    }}
                                  >
                                    <option value="">{t('Not linked', 'Sin vincular', lang)}</option>
                                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                  </select>
                                  {prodPresentations.length > 0 && (
                                    <select
                                      className="form-select form-select-sm mt-1"
                                      style={{ fontSize: 11, color: '#495057' }}
                                      value={item.presentationId || ''}
                                      onChange={e => setItemField(idx, 'presentationId', e.target.value || null)}
                                    >
                                      <option value="">{t('No presentation', 'Sin presentación', lang)}</option>
                                      {prodPresentations.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                                {/* Lot Code */}
                                <td>
                                  {suggestions.length > 0 && (
                                    <datalist id={datalistId}>
                                      {suggestions.map(lc => <option key={lc} value={lc} />)}
                                    </datalist>
                                  )}
                                  <input
                                    className="form-control form-control-sm font-monospace"
                                    placeholder={t('Lot code…', 'Código de lote…', lang)}
                                    value={item.lotCode}
                                    list={suggestions.length > 0 ? datalistId : undefined}
                                    onChange={e => setItemField(idx, 'lotCode', e.target.value.toUpperCase())}
                                    onBlur={e => { if (e.target.value) fetchLotAvailability(e.target.value, item.productId); }}
                                  />
                                  <div style={{ fontSize: 11, minHeight: 16, marginTop: 2 }}>
                                    {avail && item.lotCode ? (
                                      <span className={avail.availableUnits < 0 ? 'text-danger' : avail.availableUnits < totalUnits ? 'text-warning' : 'text-success'}>
                                        {avail.produced > 0
                                          ? `Prod: ${avail.produced} · Avail: ${avail.availableUnits}`
                                          : <span className="text-muted">{t('No production record', 'Sin registro de producción', lang)}</span>
                                        }
                                      </span>
                                    ) : <span />}
                                  </div>
                                </td>
                                {/* Expiration Date */}
                                <td>
                                  <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={item.expirationDate || ''}
                                    onChange={e => setItemField(idx, 'expirationDate', e.target.value)}
                                  />
                                </td>
                                {/* Pallet ID */}
                                <td>
                                  <input
                                    className="form-control form-control-sm text-center"
                                    value={item.palletId}
                                    onChange={e => setItemField(idx, 'palletId', e.target.value)}
                                  />
                                </td>
                                {/* Cases */}
                                <td>
                                  <input
                                    type="number" min="0"
                                    className="form-control form-control-sm text-center"
                                    value={item.quantityCases}
                                    onChange={e => setItemField(idx, 'quantityCases', e.target.value)}
                                  />
                                </td>
                                {/* Units/Case */}
                                <td>
                                  <input
                                    type="number" min="0"
                                    className="form-control form-control-sm text-center"
                                    placeholder="—"
                                    value={item.unitsPerCase}
                                    onChange={e => setItemField(idx, 'unitsPerCase', e.target.value)}
                                    title={t('Units per case — used for traceability', 'Unidades por caja — usado para trazabilidad', lang)}
                                  />
                                  <div style={{ fontSize: 11, minHeight: 16, marginTop: 2 }}>
                                    {item.unitsPerCase && item.quantityCases
                                      ? <span className="text-muted">= {totalUnits.toLocaleString()} {t('units', 'unidades', lang)}</span>
                                      : <span />}
                                  </div>
                                </td>
                                {/* Net Weight */}
                                <td>
                                  <div className="input-group input-group-sm">
                                    <input
                                      className="form-control form-control-sm"
                                      placeholder="0"
                                      value={item.netWeightValue}
                                      onChange={e => setItemField(idx, 'netWeightValue', e.target.value)}
                                      onBlur={() => handleItemWeightBlur(idx, 'netWeightValue')}
                                      title="Supports expressions: e.g. 5*12 or 30+15"
                                    />
                                    <select
                                      className="form-select form-select-sm"
                                      style={{ flex: '0 0 75px' }}
                                      value={item.netWeightUnit}
                                      onChange={e => {
                                        const newUnit = e.target.value;
                                        const oldUnit = item.netWeightUnit;
                                        setForm(f => {
                                          const items = [...f.items];
                                          const it = { ...items[idx], netWeightUnit: newUnit };
                                          if (it.netWeightValue !== '' && newUnit !== oldUnit) {
                                            const v = Number(it.netWeightValue);
                                            if (v) it.netWeightValue = String(Math.round((newUnit === 'kg' ? v * LB_TO_KG : v * KG_TO_LB) * 10) / 10);
                                          }
                                          items[idx] = it;
                                          return { ...f, items };
                                        });
                                      }}
                                    >
                                      <option value="lb">lb</option>
                                      <option value="kg">kg</option>
                                    </select>
                                  </div>
                                  <div style={{ fontSize: 11, minHeight: 16, marginTop: 2 }}>
                                    {netSuggestion && item.netWeightValue === '' ? (
                                      <button
                                        className="btn btn-link p-0 text-primary"
                                        style={{ fontSize: 11 }}
                                        onClick={() => {
                                          setItemField(idx, 'netWeightValue', netSuggestion.value);
                                          setItemField(idx, 'netWeightUnit', netSuggestion.unit);
                                        }}
                                      >
                                        {t('Use', 'Usar', lang)} {netSuggestion.value} {netSuggestion.unit}
                                      </button>
                                    ) : <span />}
                                  </div>
                                </td>
                                {/* Gross Weight */}
                                <td>
                                  <div className="input-group input-group-sm">
                                    <input
                                      className="form-control form-control-sm"
                                      placeholder="0"
                                      value={item.grossWeightValue}
                                      onChange={e => setItemField(idx, 'grossWeightValue', e.target.value)}
                                      onBlur={() => handleItemWeightBlur(idx, 'grossWeightValue')}
                                      title="Supports expressions: e.g. 5*12 or 30+15"
                                    />
                                    <select
                                      className="form-select form-select-sm"
                                      style={{ flex: '0 0 75px' }}
                                      value={item.grossWeightUnit}
                                      onChange={e => setItemField(idx, 'grossWeightUnit', e.target.value)}
                                    >
                                      <option value="lb">lb</option>
                                      <option value="kg">kg</option>
                                    </select>
                                  </div>
                                  <div style={{ fontSize: 11, minHeight: 16, marginTop: 2 }}>
                                    {grossSuggestion && item.grossWeightValue === '' ? (
                                      <button
                                        className="btn btn-link p-0 text-primary"
                                        style={{ fontSize: 11 }}
                                        onClick={() => {
                                          setItemField(idx, 'grossWeightValue', grossSuggestion.value);
                                          setItemField(idx, 'grossWeightUnit', grossSuggestion.unit);
                                        }}
                                      >
                                        {t('Use', 'Usar', lang)} {grossSuggestion.value} {grossSuggestion.unit}
                                      </button>
                                    ) : <span />}
                                  </div>
                                </td>
                                {/* Remove */}
                                <td style={{ paddingTop: 6 }}>
                                  <button className="btn btn-outline-danger btn-sm p-0 px-1" onClick={() => removeItem(idx)} title={t('Remove', 'Eliminar', lang)} disabled={form.items.length === 1}>×</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="table-light fw-semibold small">
                            <td colSpan={5}>{t('Totals', 'Totales', lang)}</td>
                            <td></td>{/* Pallet ID */}
                            <td className="text-center">{totals.cases}</td>{/* Cases */}
                            <td></td>{/* Units/Case */}
                            <td className="text-center">{totals.netStr}</td>{/* Net Wgt */}
                            <td className="text-center">{totals.grossStr}</td>{/* Gross Wgt */}
                            <td></td>{/* delete */}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-outline-secondary btn-sm" onClick={addItem}>+ {t('Add Pallet Row', 'Agregar Fila de Pallet', lang)}</button>
                      <button className="btn btn-outline-primary btn-sm" onClick={addProductGroup}>+ {t('New Product Group', 'Nuevo Grupo de Producto', lang)}</button>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="col-12">
                    <label className="form-label small mb-1">{t('Internal Notes', 'Notas Internas', lang)} <span className="text-muted">({t('not printed', 'no se imprime', lang)})</span></label>
                    <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => setField('notes', e.target.value)} />
                  </div>

                  {/* USDA/FSIS shipment attestation — required whenever a lot on this BOL is USDA-classified.
                      Filling this in here does NOT skip the re-confirmation gate at Finalize/Print — see
                      UsdaAttestationGateModal below, which always requires an explicit re-check right
                      before either action, since a BOL can sit in draft over several days. */}
                  {formHasUsdaLot && (
                    <div className="col-12">
                      <UsdaAttestationFields
                        value={form}
                        onChange={setField}
                        userOptions={userOptions}
                        lang={lang}
                        complete={formAttestationComplete}
                        establishmentNumber={usdaEstablishmentNumber}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer flex-wrap gap-2">
                <button
                  className="btn btn-outline-secondary"
                  disabled={saving}
                  title={t('Preview / Print draft', 'Vista previa / Imprimir borrador', lang)}
                  onClick={() => {
                    localStorage.removeItem('bolPreview');
                    localStorage.setItem('bolPreview', JSON.stringify(formAsBol));
                    window.open('/bol/print?preview=1', '_blank');
                  }}
                >
                  {t('Preview / Print', 'Vista Previa / Imprimir', lang)}
                </button>
                <div className="ms-auto d-flex gap-2 flex-wrap">
                  <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>{t('Cancel', 'Cancelar', lang)}</button>
                  <button className="btn btn-outline-primary" onClick={() => handleSave(false)} disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm" /> : t('Save Draft', 'Guardar Borrador', lang)}
                  </button>
                  <button className="btn btn-success" onClick={() => handleSave(true)}
                    disabled={saving || (formHasUsdaLot && !formAttestationComplete)}
                    title={formHasUsdaLot && !formAttestationComplete ? t('Complete the USDA attestation above first.', 'Complete la atestación USDA primero.', lang) : undefined}>
                    {saving ? <span className="spinner-border spinner-border-sm" /> : t('Save & Finalize', 'Guardar y Finalizar', lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

